import { RENDER_DELAY } from '@unistocks/shared/consts';
import { useEffect, useState } from 'react';

/**
 * Delays the value change by a given delay
 * @param value The value to delay
 * @param active if true, the value will be set after the delay, if false, the return value will be undefined
 * @param delay The delay in milliseconds to wait before setting the value
 * @returns The delayed value
 */
export const useDelayedValue = <TValue>(
  value: TValue,
  active: boolean,
  delay: number
): TValue | undefined => {
  const [state, setState] = useState<TValue>();

  useEffect(() => {
    if (active) {
      const timeout = setTimeout(() => setState(value), delay);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [value, delay, active]);

  return active ? state : undefined;
};

/**
 * Delays the render by a given delay
 * @param active if true, the component will render after the delay, if false, the component will not render
 * @param delay The delay in milliseconds to wait before rendering
 * @returns @boolean Whether to render or not
 */
export const useDelayedRender = (delay = RENDER_DELAY) => !!useDelayedValue(true, true, delay);
